import { useEffect } from "react";
import {
  Divider,
  Grid,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { SafeDateInput } from "@/components/SafeDateInput";
import { api } from "@/lib/api";
import { qk } from "@/lib/apiHelpers";
import type { Carrier, CarriersListResponse, Driver } from "@/types/api";

export interface DriverFormValues {
  full_name: string;
  phone: string;
  mobile: string;
  inn: string;
  citizenship: string;
  // ВУ
  license_category: string;
  license_number: string;
  license_issued_at: Date | null;
  license_expires_at: Date | null;
  license_issued_by: string;
  // Паспорт
  passport_series: string;
  passport_number: string;
  passport_issued_by: string;
  passport_issued_at: Date | null;
  foreign_passport_number: string;
  // Медицина
  medical_cert_expires_at: Date | null;
  // Привязка
  carrier_id: string;
  notes: string;
}

export type DriverFormApi = ReturnType<typeof useForm<DriverFormValues>>;

function parseDate(s?: string): Date | null {
  if (!s) return null;
  const d = dayjs(s);
  return d.isValid() ? d.toDate() : null;
}

const CITIZENSHIP_OPTIONS = [
  { value: "RU", label: "РФ" },
  { value: "BY", label: "Беларусь" },
  { value: "KZ", label: "Казахстан" },
  { value: "UZ", label: "Узбекистан" },
  { value: "OTHER", label: "Другое" },
];

const LICENSE_CATEGORIES = [
  { value: "B", label: "B" },
  { value: "C", label: "C" },
  { value: "CE", label: "CE" },
  { value: "D", label: "D" },
];

const innRule = (s: string) => {
  if (!s) return null;
  return /^\d{10}$|^\d{12}$/.test(s) ? null : "ИНН: 10 или 12 цифр";
};

export function DriverForm({
  initial,
  onChange,
}: {
  initial?: Driver;
  onChange: (api: DriverFormApi) => void;
}) {
  const form = useForm<DriverFormValues>({
    initialValues: {
      full_name: initial?.full_name ?? "",
      phone: initial?.phone ?? "",
      mobile: initial?.mobile ?? "",
      inn: initial?.inn ?? "",
      citizenship: initial?.citizenship ?? "",
      license_category: initial?.license_category ?? "",
      license_number: initial?.license_number ?? "",
      license_issued_at: parseDate(initial?.license_issued_at),
      license_expires_at: parseDate(initial?.license_expires_at),
      license_issued_by: initial?.license_issued_by ?? "",
      passport_series: initial?.passport_series ?? "",
      passport_number: initial?.passport_number ?? "",
      passport_issued_by: initial?.passport_issued_by ?? "",
      passport_issued_at: parseDate(initial?.passport_issued_at),
      foreign_passport_number: initial?.foreign_passport_number ?? "",
      medical_cert_expires_at: parseDate(initial?.medical_cert_expires_at),
      carrier_id: initial?.carrier_id ?? "",
      notes: initial?.notes ?? "",
    },
    validate: {
      full_name: (v) => (v.trim() ? null : "Обязательно"),
      phone: (v) => (v.trim() ? null : "Обязательно"),
      license_number: (v) => (v.trim() ? null : "Обязательно"),
      passport_series: (v) => (v.trim() ? null : "Обязательно"),
      passport_number: (v) => (v.trim() ? null : "Обязательно"),
      inn: innRule,
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  useEffect(() => {
    onChange(form);
  });

  const { data: carriersData } = useQuery({
    queryKey: qk.carriers({ page: 1, page_size: 200 }),
    queryFn: async () =>
      (await api.get<CarriersListResponse>("/api/v1/carriers?page=1&page_size=200")).data,
  });

  const carrierOptions = (carriersData?.items ?? []).map((c: Carrier) => ({
    value: c.id,
    label: c.name,
  }));

  return (
    <Stack gap="sm">
      {/* ── Основное (имя/контакты/ИНН) ────────────────────────────────── */}
      <Title order={5}>Основное</Title>
      <Grid gutter="sm">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="ФИО" required {...form.getInputProps("full_name")} />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 3 }}>
          <TextInput label="Телефон" required {...form.getInputProps("phone")} />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 3 }}>
          <TextInput label="Мобильный" {...form.getInputProps("mobile")} />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 4 }}>
          <TextInput
            label="ИНН"
            placeholder="10 или 12 цифр"
            {...form.getInputProps("inn")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 4 }}>
          <Select
            label="Гражданство"
            placeholder="Выберите"
            data={CITIZENSHIP_OPTIONS}
            clearable
            {...form.getInputProps("citizenship")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput
            label="Загранпаспорт"
            placeholder="номер"
            {...form.getInputProps("foreign_passport_number")}
          />
        </Grid.Col>
      </Grid>

      <Divider />

      {/* ── Паспорт (всё в одной строке) ───────────────────────────────── */}
      <Title order={5}>Паспорт РФ</Title>
      <Grid gutter="sm">
        <Grid.Col span={{ base: 4, sm: 2 }}>
          <TextInput
            label="Серия"
            placeholder="6700"
            required
            {...form.getInputProps("passport_series")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 4, sm: 2 }}>
          <TextInput
            label="Номер"
            placeholder="123456"
            required
            {...form.getInputProps("passport_number")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 4, sm: 3 }}>
          <SafeDateInput
            label="Дата выдачи"
            {...form.getInputProps("passport_issued_at")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 5 }}>
          <TextInput
            label="Кем выдан"
            placeholder="ОВД г. Смоленска"
            {...form.getInputProps("passport_issued_by")}
          />
        </Grid.Col>
      </Grid>

      <Divider />

      {/* ── ВУ (всё в одной строке) ────────────────────────────────────── */}
      <Title order={5}>Водительское удостоверение</Title>
      <Grid gutter="sm">
        <Grid.Col span={{ base: 4, sm: 2 }}>
          <Select
            label="Категория"
            placeholder="—"
            data={LICENSE_CATEGORIES}
            clearable
            {...form.getInputProps("license_category")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 8, sm: 3 }}>
          <TextInput
            label="Номер"
            placeholder="50 12 345678"
            required
            {...form.getInputProps("license_number")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 3 }}>
          <SafeDateInput
            label="Дата выдачи"
            {...form.getInputProps("license_issued_at")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 4 }}>
          <SafeDateInput
            label="Действителен до"
            {...form.getInputProps("license_expires_at")}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label="Кем выдано ВУ"
            placeholder="МО ГИБДД ГУ МВД России по Смоленской области"
            {...form.getInputProps("license_issued_by")}
          />
        </Grid.Col>
      </Grid>

      <Divider />

      {/* ── Дополнительно ──────────────────────────────────────────────── */}
      <Title order={5}>Дополнительно</Title>
      <Grid gutter="sm">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select
            label="Перевозчик"
            placeholder="Выберите перевозчика"
            data={carrierOptions}
            clearable
            searchable
            {...form.getInputProps("carrier_id")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <SafeDateInput
            label="Медкнижка действительна до"
            {...form.getInputProps("medical_cert_expires_at")}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            label="Заметки"
            placeholder="Любая дополнительная информация"
            {...form.getInputProps("notes")}
            autosize
            minRows={2}
            maxRows={4}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
